/**
 * AppPlusDataService — year-keyed APP Plus reference data.
 *
 * Composition rule (docs/reference/app-plus/README.md): reads benchmark rows
 * straight from the {year}-mips-data benchmark file WITHOUT MipsDataService's
 * Medicare-CQM/Claims filter, and layers the {year}-app-data supplements on
 * top. Deliberately a separate class from MipsDataService: the two programs
 * filter the same file differently, and this service must never mutate the
 * MIPS singleton's loaded state.
 *
 * ⚠ D13 (year-compartmentalized logic): the RESOLUTION RULES in here
 * (getBenchmarkCell's flat/historical/none logic, CAHPS SSM validation) are
 * the **2026 implementation**, surfaced through `src/lib/app-plus/years/2026`.
 * UI code resolves them via `logicForYear(programYear)` — a later year that
 * changes how benchmarks are resolved (e.g. 2027 performance-period
 * benchmarks) overrides in ITS module, not by editing this file's behavior
 * for 2026. Raw year DATA keeps living in src/data/{year}-app-data.
 *
 * (P2 — docs/reference/app-plus/IMPLEMENTATION_PLAN.md)
 */
import vbcaBenchmarks2026 from '../data/2026-mips-data/vbca_2026_quality_benchmarks.json';
import appMeasureSet2026 from '../data/2026-app-data/app_measure_set.json';
import appFlatCells2026 from '../data/2026-app-data/app_flat_benchmark_cells.json';
import appConstants2026 from '../data/2026-app-data/app_program_constants.json';
import appAdminClaims2026 from '../data/2026-app-data/app_admin_claims_scoring.json';
import { parseDecileRange, type DecileBenchmark } from './scoring-core';

// ===== BINDING TYPES =====

export type AppCollectionType =
  | 'eCQM'
  | 'MIPS CQM'
  | 'Medicare CQM'
  | 'CAHPS Survey Vendor'
  | 'CMS Administrative Claims';

export type AppBucket = 'clinical' | 'cahps' | 'adminClaims';

/** One of the 8 slots (mirrors app_measure_set.json rows). */
export interface AppMeasure {
  measureId: string;
  sspVariantId: string | null;
  name: string;
  bucket: AppBucket;
  isOutcome: boolean;
  isInverse: boolean;
  caseMinimum: number;
  collectionTypes: AppCollectionType[];
}

/**
 * Benchmark resolution for one (measure × collection type) cell.
 * kind:
 *  - 'historical'  — real decile row, scored normally
 *  - 'flat'        — real decile row flagged flat (uniform bands by policy)
 *  - 'none'        — no benchmark exists yet → UNKNOWABLE display state, and
 *                    (if it never posts) an APP exclusion that ARMS the
 *                    §425.512(a)(7) floor (unlike case-minimum exclusions)
 */
export interface AppBenchmarkCell {
  measureId: string;
  collectionType: AppCollectionType;
  kind: 'historical' | 'flat' | 'none';
  /** Decile row (VbcaBenchmark-shaped) when kind !== 'none'. */
  row: (DecileBenchmark & { isInverse: string; measureId: string }) | null;
  /** The benchmark-file row id actually used (e.g. '112SSP' vs '001'). */
  benchmarkRowId: string | null;
}

/** One CAHPS Summary Survey Measure with its own decile table. */
export interface CahpsSsm {
  /** Stable snake_case id (matches cahps_ssm_benchmarks_2026.csv ssm_id). */
  ssmId: string;
  title: string;
  row: DecileBenchmark;
}

export interface AppProgramConstants {
  programYear: number;
  qpsThreshold: number;
  scoreFloor: number;
  eucFloor: number;
  dataCompletenessThreshold: number;
  dcMissPointsSmallPractice: number;
  dcMissPointsLargePractice: number;
  smallPracticeClinicianCap: number;
  complexOrgBonusPerEcqm: number;
  complexOrgBonusCapPct: number;
  smallPracticeBonus: number;
  improvementScoreCap: number;
  percentileLegTenthMinPoints: number;
  percentileLegFortiethMinPoints: number;
  percentileLegStatus: 'derived' | 'confirmed';
  sharingRatePct: Record<'BASIC_A' | 'BASIC_B' | 'BASIC_C' | 'BASIC_D' | 'BASIC_E' | 'ENHANCED', number>;
  enhancedLossFloorPct: number;
  enhancedLossCapPct: number;
  enhancedLossNeitherMetPct: number;
  basicLossRatePct: number;
  enhancedRecoupmentCapPct: number;
  paymentCapPct: Record<'BASIC' | 'ENHANCED', number>;
  appWeightsPct: { quality: number; pi: number; ia: number; cost: number };
  iaAutoCreditPct: number;
  reweightedQualityPct: number;
  reweightedIaPct: number;
  mipsThreshold: number;
  mipsMaxNegativeAdjustmentPct: number;
  mipsFullNegativeAtOrBelow: number;
  paymentYearOffset: number;
  cpbMax: number;
  cpbDefault: number;
}

export interface AdminClaimsMeasureParams {
  caseMinimum: number;
  median: number | null;
  sd: number | null;
  paramSource: string | null;
  priorYearMedian: number | null;
  priorYearSd: number | null;
  priorYearSource: string | null;
}

export interface AdminClaimsScoringConfig {
  programYear: number;
  minClinicians: number;
  medianAnchorPoints: number;
  /** decile (1-10, as string keys in JSON) → SD multiplier off the median */
  sdMultipliers: Record<string, number>;
  measures: Record<'479' | '484', AdminClaimsMeasureParams>;
}

// ===== SERVICE =====

/** The 9 SSM ids in canonical (benchmark-file) order. */
const SSM_IDS_IN_FILE_ORDER: ReadonlyArray<{ ssmId: string; title: string }> = [
  { ssmId: 'timely_care',            title: 'Getting Timely Care, Appointments, and Information' },
  { ssmId: 'provider_communication', title: 'How Well Providers Communicate' },
  { ssmId: 'rating_of_provider',     title: "Patient's Rating of Provider" },
  { ssmId: 'access_to_specialists',  title: 'Access to Specialists' },
  { ssmId: 'health_promotion',       title: 'Health Promotion and Education' },
  { ssmId: 'shared_decision_making', title: 'Shared Decision Making' },
  { ssmId: 'care_coordination',      title: 'Care Coordination' },
  { ssmId: 'office_staff',           title: 'Courteous and Helpful Office Staff' },
  { ssmId: 'stewardship',            title: 'Stewardship of Patient Resources' },
];

interface RawBenchmarkRow extends DecileBenchmark {
  measureId: string;
  measureTitle?: string;
  submissionMethods: string;
  isInverse: string;
  [key: string]: unknown;
}

interface RawMeasureSet {
  compositeSlots: number;
  measures: unknown[];
  outcomeMeasures: string[];
  remainingMeasures: string[];
}

interface RawFlatCells {
  flatCells: Array<{ measureId: string; collectionType: string; benchmarkRowId: string }>;
  noBenchmarkCells: Array<{ measureId: string; collectionType: string }>;
}

interface YearDataset {
  benchmarks: RawBenchmarkRow[];
  measureSet: RawMeasureSet;
  flatCells: RawFlatCells;
  constants: AppProgramConstants;
  adminClaims: AdminClaimsScoringConfig;
}

export class AppPlusDataService {
  private static instance: AppPlusDataService;

  private static readonly DATASETS: Record<number, YearDataset> = {
    2026: {
      benchmarks: vbcaBenchmarks2026 as unknown as RawBenchmarkRow[],
      measureSet: appMeasureSet2026 as unknown as RawMeasureSet,
      flatCells: appFlatCells2026 as unknown as RawFlatCells,
      constants: appConstants2026 as unknown as AppProgramConstants,
      adminClaims: appAdminClaims2026 as unknown as AdminClaimsScoringConfig,
    },
  };

  private constructor() {}

  static getInstance(): AppPlusDataService {
    if (!AppPlusDataService.instance) {
      AppPlusDataService.instance = new AppPlusDataService();
    }
    return AppPlusDataService.instance;
  }

  /** BINDING: years with an APP dataset (drives module supportedYears in P4). */
  getSupportedYears(): number[] {
    return Object.keys(AppPlusDataService.DATASETS).map(Number).sort();
  }

  private dataset(year: number): YearDataset {
    const ds = AppPlusDataService.DATASETS[year];
    if (!ds) {
      throw new Error(
        `[AppPlusDataService] No APP dataset for year ${year}. Supported: ${this.getSupportedYears().join(', ')}`
      );
    }
    return ds;
  }

  /** BINDING signature. The composite measure set for a year (incl. the
   *  incentive-pathway groupings straight from app_measure_set.json). */
  getMeasureSet(year: number): {
    compositeSlots: number;
    measures: AppMeasure[];
    outcomeMeasures: string[];
    remainingMeasures: string[];
  } {
    const ds = this.dataset(year);
    return {
      compositeSlots: ds.measureSet.compositeSlots,
      measures: ds.measureSet.measures as AppMeasure[],
      outcomeMeasures: ds.measureSet.outcomeMeasures,
      remainingMeasures: ds.measureSet.remainingMeasures,
    };
  }

  getConstants(year: number): AppProgramConstants {
    return this.dataset(year).constants;
  }

  getAdminClaimsConfig(year: number): AdminClaimsScoringConfig {
    return this.dataset(year).adminClaims;
  }

  /**
   * BINDING signature. Resolve the benchmark for one (measure × collection
   * type) cell. Resolution order:
   *  1. explicit noBenchmarkCells entry → kind 'none'
   *  2. explicit flatCells entry → kind 'flat', row looked up by benchmarkRowId
   *  3. SSP-variant row for the collection type → 'historical'
   *  4. base-id row for the collection type → 'historical'
   *  5. row exists but is all '--' (insufficient-volume shape) → 'none'
   *  6. no row at all → 'none'
   */
  getBenchmarkCell(year: number, measureId: string, collectionType: AppCollectionType): AppBenchmarkCell {
    const ds = this.dataset(year);
    const none: AppBenchmarkCell = { measureId, collectionType, kind: 'none', row: null, benchmarkRowId: null };

    const noBench = ds.flatCells.noBenchmarkCells.find(
      c => c.measureId === measureId && c.collectionType === collectionType
    );
    if (noBench) return none;

    const measure = (ds.measureSet.measures as AppMeasure[]).find(m => m.measureId === measureId);
    const findRow = (rowId: string) =>
      ds.benchmarks.find(r => r.measureId === rowId && r.submissionMethods === collectionType);

    const flat = ds.flatCells.flatCells.find(
      c => c.measureId === measureId && c.collectionType === collectionType
    );
    if (flat) {
      const row = findRow(flat.benchmarkRowId) ?? null;
      return row && this.rowHasDeciles(row)
        ? { measureId, collectionType, kind: 'flat', row, benchmarkRowId: flat.benchmarkRowId }
        : none;
    }

    const candidates = [measure?.sspVariantId, measureId].filter(Boolean) as string[];
    for (const rowId of candidates) {
      const row = findRow(rowId);
      if (row && this.rowHasDeciles(row)) {
        return { measureId, collectionType, kind: 'historical', row, benchmarkRowId: rowId };
      }
    }
    return none;
  }

  /** All-'--' rows (e.g. 113 MIPS CQM "insufficient volume") are not benchmarks. */
  private rowHasDeciles(row: DecileBenchmark): boolean {
    for (let i = 1; i <= 10; i++) {
      const { min, max } = parseDecileRange(row[`decile_${i}` as keyof DecileBenchmark]);
      if (min !== null || max !== null) return true;
    }
    return false;
  }

  /**
   * BINDING signature. The nine CAHPS SSMs with their decile tables, resolved
   * by measureTitle from the year's benchmark file (order-independent).
   * Throws if any of the nine is missing — a loud failure beats a silently
   * shrunken CAHPS average.
   */
  getCahpsSsms(year: number): CahpsSsm[] {
    const ds = this.dataset(year);
    const rows321 = ds.benchmarks.filter(
      r => r.measureId === '321' && r.submissionMethods === 'CAHPS Survey Vendor'
    );
    return SSM_IDS_IN_FILE_ORDER.map(({ ssmId, title }) => {
      const row = rows321.find(r => r.measureTitle === title);
      if (!row) {
        throw new Error(
          `[AppPlusDataService] CAHPS SSM "${title}" not found in ${year} benchmarks — check measureTitle fields on the 321 rows`
        );
      }
      return { ssmId, title, row };
    });
  }
}
